import { ELEMENT_TYPES } from 'cg/components/documents/editor/slate/types'
import { Transforms, Path, Editor, Point, Range, Node, Element as SlateElement } from 'vendor/slate'
import { ReactEditor } from 'vendor/slate-react'
import { v4 as uuidv4 } from 'uuid'

const insertTable = (editor, path) => {
  const table = {
    type: ELEMENT_TYPES.table,
    children: [
      {
        type: ELEMENT_TYPES.table_thead,
        parent: { type: ELEMENT_TYPES.table },
        children: [
          {
            type: ELEMENT_TYPES.table_tr,
            parent: { type: ELEMENT_TYPES.table_thead },
            children: [
              {
                id: `table-thead-${uuidv4()}`,
                type: ELEMENT_TYPES.table_th,
                parent: { type: ELEMENT_TYPES.table_tr },
                children: [
                  {
                    type: ELEMENT_TYPES.default,
                    parent: { type: ELEMENT_TYPES.table_th },
                    children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }]
                  }
                ]
              },
              {
                id: `table-thead-${uuidv4()}-1`,
                type: ELEMENT_TYPES.table_th,
                parent: { type: ELEMENT_TYPES.table_tr },
                children: [
                  {
                    type: ELEMENT_TYPES.default,
                    parent: { type: ELEMENT_TYPES.table_th },
                    children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        type: ELEMENT_TYPES.table_tbody,
        parent: { type: ELEMENT_TYPES.table },
        children: [
          {
            type: ELEMENT_TYPES.table_tr,
            parent: { type: ELEMENT_TYPES.table_tbody },
            children: [
              {
                id: `table-tbody-${uuidv4()}`,
                type: ELEMENT_TYPES.table_td,
                parent: { type: ELEMENT_TYPES.table_tr },
                children: [
                  {
                    type: ELEMENT_TYPES.default,
                    parent: { type: ELEMENT_TYPES.table_td },
                    children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }]
                  }
                ]
              },
              {
                id: `table-tbody-${uuidv4()}-1`,
                type: ELEMENT_TYPES.table_td,
                parent: { type: ELEMENT_TYPES.table_tr },
                children: [
                  {
                    type: ELEMENT_TYPES.default,
                    parent: { type: ELEMENT_TYPES.table_td },
                    children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  }

  Transforms.insertNodes(editor, table, { at: path, select: true })
  ReactEditor.focus(editor)
}

const withTables = editor => {
  const { deleteBackward, deleteForward, deleteFragment } = editor

  editor.deleteBackward = unit => {
    const { selection } = editor

    if (selection && Range.isCollapsed(selection)) {
      const { anchor } = selection
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n => !Editor.isEditor(n) && [ELEMENT_TYPES.table_td, ELEMENT_TYPES.table_th].includes(n.parent && n.parent.type)
      })

      if (match) {
        const [, path] = match
        const start = Editor.start(editor, Path.parent(path))

        if (Point.equals(anchor, start)) {
          return
        }
      }
    }

    deleteBackward(unit)
  }

  editor.deleteForward = unit => {
    const { selection } = editor

    if (selection && Range.isCollapsed(selection)) {
      const { anchor } = selection
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n => !Editor.isEditor(n) && [ELEMENT_TYPES.table_td, ELEMENT_TYPES.table_th].includes(n.parent && n.parent.type)
      })

      if (match) {
        const [node, path] = match
        if (node) {
          const end = Editor.end(editor, Path.parent(path))

          if (Point.equals(anchor, end)) {
            return
          }
        }
      }
    }

    deleteForward(unit)
  }

  editor.deleteFragment = () => {
    const { selection } = editor

    if (selection) {
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n =>
          !Editor.isEditor(n) &&
          [ELEMENT_TYPES.table_td, ELEMENT_TYPES.table_tr, ELEMENT_TYPES.table_th].includes(n.parent && n.parent.type)
      })

      if (match) {
        const [start, end] = Range.edges(selection)

        if (start.path.length !== end.path.length) return

        const startParentPath = Path.parent(start.path)
        const endParentPath = Path.parent(end.path)

        const startGrandParentPath = Node.has(editor, startParentPath) ? Path.parent(startParentPath) : null
        const endGrandParentPath = Node.has(editor, endParentPath) ? Path.parent(endParentPath) : null

        const startPathNode = Node.has(editor, startParentPath) ? Node.get(editor, startParentPath) : null
        const endPathNode = Node.has(editor, endParentPath) ? Node.get(editor, endParentPath) : null

        if (
          startPathNode &&
          endPathNode &&
          startPathNode.parent &&
          endPathNode.parent &&
          startPathNode.parent.type !== endPathNode.parent.type
        )
          return

        if (startGrandParentPath && endGrandParentPath && !Path.equals(startGrandParentPath, endGrandParentPath)) return
      }
    }
    deleteFragment()
  }

  return editor
}

export { insertTable, withTables }
